/**
 * InfinitSales - Request Cache
 *
 * Cache de módulo (TTL + dedup de requisição em voo) para GETs de
 * catálogo/estado que vários componentes disparam ao mesmo tempo — em prod
 * a mesma carga fazia /companies/me 2x, /companies/limits 2x,
 * /subscription/status 3x, /categories 2x, /workspaces 2x (efeitos com deps
 * instáveis + consumidores independentes sem compartilhamento).
 *
 * Combina os dois padrões que já existem no repo:
 *  - TTL + forceRefresh + invalidate (followupService._limitsCache)
 *  - promise em voo compartilhada (useFeatureFlags._inflight)
 *
 * Uso (dentro de um service):
 *   const data = await requestCache.cachedGet('company:me', fetcher, { ttl: 300_000 });
 *   requestCache.invalidate('company:');   // prefixo — nas mutações
 *   requestCache.clearAll();               // logout / troca de conta
 *
 * Regras:
 *  - Rejeição NÃO é cacheada (a promise em voo é descartada no erro).
 *  - Chaves não levam user_id: o cache vive por aba/sessão. O logout MANUAL
 *    navega via SPA (sem reload!), então o AuthContext chama clearAll() em
 *    logout/login/register — sem isso o próximo usuário da mesma aba leria
 *    empresa/plano da conta anterior.
 *  - Um fetch só grava/limpa o cache se ainda for o "dono" da chave em
 *    _inflight — invalidate() no meio de um GET em voo impede que a resposta
 *    PRÉ-mutação repopule o cache por cima do refetch fresco.
 */

const _cache = new Map(); // key -> { expiresAt, value }
const _inflight = new Map(); // key -> Promise

const requestCache = {
  /**
   * @param {string} key - chave estável, prefixada por domínio (ex.: 'company:me')
   * @param {() => Promise<any>} fetcher - executa o GET real
   * @param {{ ttl?: number, force?: boolean }} opts - ttl em ms (default 60s)
   */
  async cachedGet(key, fetcher, { ttl = 60_000, force = false } = {}) {
    if (!force) {
      const entry = _cache.get(key);
      if (entry && Date.now() < entry.expiresAt) return entry.value;
      const pending = _inflight.get(key);
      if (pending) return pending;
    }

    const promise = Promise.resolve()
      .then(fetcher)
      .then((value) => {
        // Só commita se esta promise ainda é a registrada para a chave —
        // se um invalidate() rodou no meio (mutação), o resultado velho vai
        // só para quem já esperava por ele, sem repopular o cache nem
        // derrubar o marcador em voo do refetch novo.
        if (_inflight.get(key) === promise) {
          _cache.set(key, { expiresAt: Date.now() + ttl, value });
          _inflight.delete(key);
        }
        return value;
      })
      .catch((err) => {
        if (_inflight.get(key) === promise) {
          _inflight.delete(key);
        }
        throw err;
      });

    _inflight.set(key, promise);
    return promise;
  },

  /** Derruba toda chave que começa com o prefixo (ex.: 'company:'). */
  invalidate(prefix) {
    for (const key of _cache.keys()) {
      if (key.startsWith(prefix)) _cache.delete(key);
    }
    for (const key of _inflight.keys()) {
      if (key.startsWith(prefix)) _inflight.delete(key);
    }
  },

  clearAll() {
    _cache.clear();
    _inflight.clear();
  },
};

export default requestCache;
